import { Dispatch } from 'redux';
import up = require('updeep');
import { throttle, values } from 'lodash';
import * as selectors from '../selectors';
import { State, StoryQuery, defaultState, Modals } from '../state';
import { Story, Stories, ImageResponse, TaggedUserIDs, CompetitionPledge } from '../../userApi';
import { patch } from './patch';
import { handleUnexpectedError } from './handleUnexpectedError';
import { ActionType } from './types';
import { postImage } from './image';
import { finishActionAndGoHome } from './completeAction';
import { toast } from './toast';
import { ExtraArguments } from '../extraArguments';
import logger from '../../utils/logger';
import { getStoryParams, makePledgePath } from '../../utils/routerHelper';
import { addUserPoints } from './score';
import { trackEvent } from '../../utils/googleAnalytics';
import { CompetitionPledges, getValidationErrors, isSuccessfulResponse } from '../../utils/userApiHelper';
import { ValidationError } from '../../userApi';
import { StyleState } from '../../utils/styles';

export const openConfirmStoryDelete = (story: Story) => async (dispatch: Dispatch, getState: () => State) => {
  dispatch(patch({
    modal: Modals.ConfirmStoryDelete,
    storyToDelete: story
  }));
};

export const closeConfirmStoryDelete = () => async (dispatch: Dispatch, getState: () => State) => {
  dispatch(patch({
    modal: Modals.None,
    storyToDelete: null
  }));
};

export const openFlagStoryModal = (story: Story) => (dispatch: Dispatch) => {
  dispatch(patch({
    modal: Modals.FlagStory,
    storyToFlag: story
  }));
};

export const closeFlagStoryModal = () => (dispatch: Dispatch) => {
  dispatch(patch({
    modal: Modals.None,
    storyToFlag: null
  }));
};

const mergeStoryReactionMaps = <T extends Record<number, boolean>>(previous: T | undefined, incoming: T | undefined): T => ({
  ...(previous || {}),
  ...(incoming || {})
} as T);

/** Normalize API story-feed keys (storyCommentLikes) onto Redux state names (storyCommentsLikes). */
export const normalizeStoryFeedPayload = (res: any, previous: Partial<State> = {}): Partial<State> => {
  if (!res) return {};
  const stories = Array.isArray(res.stories) ? res.stories : [];
  return {
    stories: up.constant(stories),
    storyPagination: up.constant(res.storyPagination || { nextPage: -1 }),
    storyLikes: mergeStoryReactionMaps(previous.storyLikes as any, res.storyLikes),
    storyFlags: mergeStoryReactionMaps(previous.storyFlags as any, res.storyFlags),
    storyComments: { ...(previous.storyComments || {}), ...(res.storyComments || {}) },
    storyCommentPagination: { ...(previous.storyCommentPagination || {}), ...(res.storyCommentPagination || {}) },
    storyCommentsLikes: mergeStoryReactionMaps(
      previous.storyCommentsLikes as any,
      res.storyCommentsLikes || res.storyCommentLikes
    ),
    storyCommentsFlags: mergeStoryReactionMaps(
      previous.storyCommentsFlags as any,
      res.storyCommentsFlags || res.storyCommentFlags
    ),
    storyTaggedUserIDs: {
      ...(previous.storyTaggedUserIDs || {}),
      ...(res.storyTaggedUserIDs || {})
    },
    users: {
      ...(previous.users || {}),
      ...(res.users || {})
    }
  };
};

/** Merge just-posted local stories into a server page without breaking newest-first order. */
export const mergeStoriesNewestFirst = (
  localExtras: Story[],
  serverStories: Story[],
  retainWindowMs: number | null = 5 * 60 * 1000
): Story[] => {
  const now = Date.now();
  const byID: { [id: number]: Story } = {};
  (serverStories || []).forEach((story) => {
    if (story && story.id) byID[story.id] = story;
  });
  (localExtras || []).forEach((story) => {
    if (!story || !story.id || byID[story.id]) return;
    if (retainWindowMs != null) {
      const created = new Date(story.createdAt).getTime();
      // Only keep very recent local posts (upload race); never re-surface older retained rows.
      if (!Number.isFinite(created) || (now - created) > retainWindowMs) return;
    }
    byID[story.id] = story;
  });
  return Object.keys(byID)
    .map((id) => byID[Number(id)])
    .sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      if (tb !== ta) return tb - ta;
      return (b.id || 0) - (a.id || 0);
    });
};

const isMainCompetitionStoryFeed = (storyQuery: StoryQuery) =>
  !storyQuery.userID && !storyQuery.entityID && !storyQuery.competitionPledgeID && !storyQuery.storyID;

/** True when silently prepending new posts won't steal focus or scroll position. */
export const feedRefreshIsQuiet = (state: State): boolean => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  if (document.hidden) {
    return false;
  }
  if (state.modal && state.modal !== Modals.None) {
    return false;
  }
  if (state.editingStoryID || state.spinner) {
    return false;
  }
  const active = document.activeElement as HTMLElement | null;
  if (active) {
    const tag = (active.tagName || '').toLowerCase();
    if (tag === 'textarea' || tag === 'input' || active.isContentEditable) {
      return false;
    }
  }
  const scrollY = window.scrollY || window.pageYOffset || 0;
  return scrollY <= 320;
};

/** Reload the competition activity feed from /competitions/:id/stories (source of truth). */
export const refreshCompetitionFeed = () =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    if (!state.competition || !state.competition.id) {
      return;
    }
    const api = selectors.userApi(state);
    try {
      const res = await api.getCompetitionStories(
        state.competition.id,
        0,
        0,
        0,
        0,
        1,
        state.storyTerm || '',
        state.token
      );
      const retained = (getState().stories || []).filter((s) => s && s.id);
      const serverStories = res.stories || [];
      dispatch(patch({
        ...normalizeStoryFeedPayload(
          { ...res, stories: mergeStoriesNewestFirst(retained, serverStories) },
          getState()
        ),
        pendingFeedStories: up.constant([])
      }));
    } catch (err) {
      handleUnexpectedError(err)(dispatch, getState);
    }
  };

/**
 * Soft live refresh for the main competition feed: prepend new posts when quiet,
 * otherwise park them behind a "new posts" chip so scroll/commenting stay stable.
 */
export const softPollCompetitionFeed = () =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    if (!state.competition || !state.competition.id || !state.token) {
      return;
    }
    if (!isMainCompetitionStoryFeed(state.storyQuery)) {
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    const api = selectors.userApi(state);
    try {
      const res = await api.getCompetitionStories(
        state.competition.id,
        0,
        0,
        0,
        0,
        1,
        state.storyTerm || '',
        state.token
      );
      const latest = getState();
      const current = (latest.stories || []).filter((s) => s && s.id);
      const currentIDs: { [id: number]: true } = {};
      current.forEach((s) => { currentIDs[s.id] = true; });
      const pending = latest.pendingFeedStories || [];
      pending.forEach((s) => { if (s && s.id) currentIDs[s.id] = true; });
      const serverStories = res.stories || [];
      const brandNew = serverStories.filter((s) => s && s.id && !currentIDs[s.id]);
      if (brandNew.length === 0) {
        return;
      }
      if (feedRefreshIsQuiet(latest)) {
        // Prepend new posts; keep paginated rows + comment threads intact.
        // Do not touch storyPagination (page-1 nextPage would rewind infinite scroll).
        dispatch(patch({
          stories: up.constant(mergeStoriesNewestFirst(current, serverStories)),
          pendingFeedStories: up.constant([]),
          users: {
            ...(latest.users || {}),
            ...(res.users || {})
          },
          storyLikes: mergeStoryReactionMaps(latest.storyLikes as any, res.storyLikes),
          storyFlags: mergeStoryReactionMaps(latest.storyFlags as any, res.storyFlags),
          storyTaggedUserIDs: {
            ...(latest.storyTaggedUserIDs || {}),
            ...(res.storyTaggedUserIDs || {})
          }
        }));
        return;
      }
      const nextPending = mergeStoriesNewestFirst(brandNew, pending, null);
      dispatch(patch({
        pendingFeedStories: up.constant(nextPending),
        users: {
          ...(latest.users || {}),
          ...(res.users || {})
        }
      }));
    } catch (_err) {
      // Quiet failure — next poll retries; don't toast on background refresh.
    }
  };

/** Apply parked new posts (from the "N new posts" chip). */
export const applyPendingFeedStories = () =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const pending = state.pendingFeedStories || [];
    if (!pending.length) {
      return;
    }
    const current = (state.stories || []).filter((s) => s && s.id);
    dispatch(patch({
      stories: up.constant(mergeStoriesNewestFirst(pending, current, null)),
      pendingFeedStories: up.constant([])
    }));
  };

const hasValidationCode = async (err: any, code: ValidationError.CodeEnum) => {
  const validationErrors = await getValidationErrors(err);
  return Boolean(validationErrors && validationErrors.some((validationError) => validationError.code === code));
};

// WARN: We are updating the whole array of stories - is there a performance concern here?
export const toggleStoryLike = (story: Story) => async (dispatch: Dispatch, getState: () => State) => {
  const { stories } = getState();
  const storyLikes = getState().storyLikes || {};
  const wasLiked = Boolean(storyLikes[story.id]);
  let likes = story.likes;
  if (wasLiked) {
    likes--;
  } else {
    likes++;
  }

  const storyIndex = stories.findIndex((s) => s.id === story.id);
  const storiesUpdate = { [storyIndex]: { ...story, likes } } as Stories;
  dispatch(patch({
    storyLikes: { [story.id]: !wasLiked },
    stories: storiesUpdate
  }));

  const api = selectors.userApi(getState());
  try {
    if (!wasLiked) {
      await api.postLike(story.id, 'story');
      trackEvent('like', 'create', story.id.toString());
    } else {
      await api.deleteLike(story.id, 'story');
      trackEvent('like', 'delete', story.id.toString());
    }
  } catch (err) {
    if (isSuccessfulResponse(err)) {
      return;
    }
    if (!wasLiked && await hasValidationCode(err, ValidationError.CodeEnum.ErrLikeAlreadyExists)) {
      dispatch(patch({
        storyLikes: { [story.id]: true },
        stories: up.updateIn(`${storyIndex}`, story, stories)
      }));
      return;
    }
    dispatch(patch({
      storyLikes: { [story.id]: wasLiked },
      stories: up.updateIn(`${storyIndex}`, story, stories)
    }));
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('like', 'unhandledError');
  }
  return;
};

export const toggleStoryFlag = (story: Story) => async (dispatch: Dispatch, getState: () => State) => {
  const storyFlags = getState().storyFlags || {};
  const wasFlagged = Boolean(storyFlags[story.id]);
  if (!wasFlagged) {
    // New reports require a written reason before submit.
    openFlagStoryModal(story)(dispatch);
    return;
  }
  await removeStoryFlag(story)(dispatch, getState);
};

export const submitStoryFlag = (reason: string) => async (dispatch: Dispatch, getState: () => State) => {
  const trimmed = (reason || '').trim();
  if (!trimmed) {
    return;
  }
  const state = getState();
  const story = state.storyToFlag;
  if (!story) {
    return;
  }
  const { stories } = state;
  const storyFlags = state.storyFlags || {};
  if (storyFlags[story.id]) {
    dispatch(closeFlagStoryModal());
    return;
  }

  const storyIndex = stories.findIndex((s) => s.id === story.id);
  const storiesUpdate = { [storyIndex]: { ...story, flags: story.flags + 1 } } as Stories;
  dispatch(patch({
    storyFlags: { [story.id]: true },
    stories: storiesUpdate,
    modal: Modals.None,
    storyToFlag: null,
    spinner: true
  }));

  const api = selectors.userApi(getState());
  try {
    await api.postFlag(story.id, 'story', { flag: { contentMD: trimmed } });
    trackEvent('flag', 'create', story.id.toString());
    dispatch(patch({ spinner: false }));
    toast({
      style: StyleState.Success,
      message: 'Thanks — your report was sent for review.'
    })(dispatch, getState);
  } catch (err) {
    dispatch(patch({ spinner: false }));
    if (isSuccessfulResponse(err)) {
      return;
    }
    if (await hasValidationCode(err, ValidationError.CodeEnum.ErrFlagAlreadyExists)) {
      dispatch(patch({
        storyFlags: { [story.id]: true },
        stories: up.updateIn(`${storyIndex}`, story, stories)
      }));
      return;
    }
    storiesUpdate[storyIndex] = story;
    dispatch(patch({
      storyFlags: { [story.id]: false },
      stories: storiesUpdate
    }));
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('flag', 'unhandledError');
  }
};

const removeStoryFlag = (story: Story) => async (dispatch: Dispatch, getState: () => State) => {
  const { stories } = getState();
  const storyFlags = getState().storyFlags || {};
  const wasFlagged = Boolean(storyFlags[story.id]);
  if (!wasFlagged) {
    return;
  }
  const flags = Math.max(0, story.flags - 1);
  const storyIndex = stories.findIndex((s) => s.id === story.id);
  const storiesUpdate = { [storyIndex]: { ...story, flags } } as Stories;
  dispatch(patch({
    storyFlags: { [story.id]: false },
    stories: storiesUpdate
  }));

  const api = selectors.userApi(getState());
  try {
    await api.deleteFlag(story.id, 'story');
    trackEvent('flag', 'delete', story.id.toString());
  } catch (err) {
    if (isSuccessfulResponse(err)) {
      return;
    }
    storiesUpdate[storyIndex] = story;
    dispatch(patch({
      storyFlags: { [story.id]: true },
      stories: storiesUpdate
    }));
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('flag', 'unhandledError');
  }
};

const pageStories = (page: number, term?: string) => async (dispatch: Dispatch, getState: () => State) => {
  const state = getState();
  const { storyPagination, stories, storyQuery, competition, token, storyTerm } = state;
  const nextPage = (storyPagination && storyPagination.nextPage != null) ? storyPagination.nextPage : -1;
  if (nextPage === -1 && page !== 0) return;
  const api = selectors.userApi(state);
  try {
    const { userID, entityID, competitionPledgeID, storyID } = storyQuery;
    const requestedPage = page === 0 ? 0 : ((storyPagination && storyPagination.nextPage != null) ? storyPagination.nextPage : 1);
    const res = await api.getCompetitionStories(competition.id, userID, entityID, competitionPledgeID, storyID, requestedPage, term || storyTerm, token);
    const storyIDs: { [storyID: number]: boolean } = stories.reduce((previous, story) => ({ ...previous, [story.id]: true }), {});
    const newStories = res.stories.filter((story) => !storyIDs[story.id]);
    const update = {
      ...res,
      storyTerm: term,
      stories: up.constant([
        ...stories,
        ...newStories
      ]),
      storyLikes: mergeStoryReactionMaps(state.storyLikes, res.storyLikes),
      storyFlags: mergeStoryReactionMaps(state.storyFlags, res.storyFlags),
      storyComments: { ...(state.storyComments || {}), ...(res.storyComments || {}) },
      storyCommentsLikes: mergeStoryReactionMaps(state.storyCommentsLikes, res.storyCommentsLikes),
      storyCommentsFlags: mergeStoryReactionMaps(state.storyCommentsFlags, res.storyCommentsFlags)
    };
    dispatch(patch(update, ActionType.GetCompetitionStories));
  } catch (err) {
    dispatch(patch({
      storyPagination: { nextPage: -1 }
    }, ActionType.GetCompetitionStories));
    handleUnexpectedError(err)(dispatch, getState);
  }
};

export const pageStoriesThrottle = throttle(pageStories, 500);

export const setStoryQuery = (storyQuery: StoryQuery) => async (dispatch: Dispatch, _getState: () => State) => dispatch(patch({
  storyQuery,
  stories: up.constant(defaultState.stories),
  pendingFeedStories: up.constant([]),
  storyPagination: up.constant({ nextPage: -1 }),
  storyComments: up.constant({}),
  storyCommentPagination: up.constant({}),
  storyCommentsFlags: up.constant({}),
  storyCommentsLikes: up.constant({}),
  storyLikes: up.constant({}),
  storyFlags: up.constant({})
}, ActionType.SetStoryQuery));

export const postCompetitionStories = (newStory: Story, taggedUserIDs: TaggedUserIDs, fileInputID: string, savedStoryFormID: string) =>
  async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const { competitionPledges } = getState();
  const { i18n, getImage } = extra;
  const t = i18n.getFixedT(i18n.language, 'story');
  dispatch(patch({ spinner: true }));

  let imageResponse: ImageResponse;
  if (getImage(fileInputID)) {
    imageResponse = await postImage(fileInputID)(dispatch, getState, extra);
    if (!imageResponse) {
      dispatch(patch({ spinner: false }));
      return;
    }
  }

  try {
    const story = {
      ...newStory
    } as Story;
    delete story.id;
    if (imageResponse) {
      story.photo = imageResponse.url;
    } else if (!story.photo) {
      delete story.photo;
    }
    let pledge: CompetitionPledge;
    if (story.competitionPledgeID !== 0) {
      pledge = competitionPledges[story.competitionPledgeID];
      if (pledge.canUseMarkdown) {
        const { converter } = await import('../../utils/markdown');
        story.contentHTML = converter.makeHtml(story.contentMD);
      }
    }
    const state = getState();
    const api = selectors.userApi(state);
    const res = await api.postCompetitionStories(state.competition.id, { story, taggedUserIDs });
    trackEvent('story', 'create', pledge && pledge.shortTitle);
    const update: Partial<State> = {
      spinner: false,
      savedStoryFormID,
      stories: up.constant([
        res.story,
        ...(state.stories || []).filter((s) => !s || s.id !== res.story.id)
      ]),
      storyTaggedUserIDs: {
        0: [],
        ...res.storyTaggedUserIDs
      },
      users: {
        ...state.users,
        ...(res.users || {}),
        [state.user.id]: state.user
      }
    };
    if (story.competitionPledgeID !== 0) {
      let latestToPledge = (state.latestToPledge && state.latestToPledge[story.competitionPledgeID]) || [];
      if (!latestToPledge.some((userID) => userID === state.user.id)) {
        latestToPledge = [
          state.user.id,
          ...latestToPledge
        ];
        update.latestToPledge = { [story.competitionPledgeID]: up.constant(latestToPledge) };
      }
    }
    dispatch(patch(update));
    const { points } = res;
    if (points && points.points > 0) {
      try {
        addUserPoints(points, story.competitionPledgeID)(dispatch, getState, extra);
      } catch (err) {
        logger.error(err);
      }
    }
    // Ensure the new story is on the home feed before navigation remounts Dashboard.
    try {
      await refreshCompetitionFeed()(dispatch, getState);
    } catch (feedErr) {
      logger.error(feedErr);
    }
    finishActionAndGoHome(dispatch, getState, extra, points && points.points > 0 ? points : undefined);
  } catch (err) {
    dispatch(patch({ spinner: false }));
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('story', 'unhandledError', 'create');
  }
};

export const editStory = (storyID: number) => patch({ editingStoryID: storyID });
export const cancelStoryEdit = () => patch({
  editingStoryID: null
});

export const highlightHero = (userID: number) => async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  dispatch(patch({ storyTaggedUserIDs: { [0]: [userID] } }));
  const { history, i18n } = extra;
  const { competitionPledges } = getState();
  const highlightAHero = values(competitionPledges).find((pledge) => pledge.uuid === CompetitionPledges.HighlightAHero);
  if (!highlightAHero) {
    handleUnexpectedError(new Error('Could not find HighlightAHero pledge.'))(dispatch, getState);
    return;
  }
  history.push(makePledgePath(highlightAHero, i18n.t));
};

export const clearNewStoryTaggedUserIDs = () => patch({ storyTaggedUserIDs: { [0]: up.constant([]) }});

export const putStory = (storyForm: Story, taggedUserIDs: TaggedUserIDs, fileInputID: string, savedStoryFormID: string) => async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const t = extra.i18n.getFixedT(extra.i18n.language, 'story');
  dispatch(patch({ spinner: true }));

  let imageResponse: ImageResponse;
  try {
    imageResponse = await postImage(fileInputID)(dispatch, getState, extra);
  } catch (err) {
    logger.error(err);
  }

  const story = {
    ...storyForm
  } as Story;
  if (imageResponse) {
    story.photo = imageResponse.url;
  } else if (!story.photo) {
    delete story.photo;
  }
  story.links = story.links || [];
  if (!story.locationQualifier) delete story.locationQualifier;

  const { stories } = getState();
  const api = selectors.userApi(getState());
  try {
    const res = await api.putStory(story.id, { story, taggedUserIDs });
    trackEvent('story', 'update');
    const storyIndex = stories.findIndex((s) => s.id === story.id);
    const previousStory = storyIndex >= 0 ? stories[storyIndex] : storyForm;
    const updatedStory = {
      ...previousStory,
      ...res.story
    };
    const updatedStories = storyIndex >= 0
      ? stories.map((existingStory, index) => index === storyIndex ? updatedStory : existingStory)
      : stories;
    const pointsDelta = (updatedStory.pointsEarned || 0) - (previousStory.pointsEarned || 0);
    const updatedTaggedUserIDs = res.storyTaggedUserIDs
      && res.storyTaggedUserIDs[story.id] !== undefined
      ? res.storyTaggedUserIDs[story.id]
      : taggedUserIDs;
    dispatch(patch({
      spinner: false,
      savedStoryFormID,
      stories: up.constant(updatedStories),
      editingStoryID: null,
      storyTaggedUserIDs: { [story.id] : up.constant(updatedTaggedUserIDs) },
      users: {
        ...getState().users,
        ...(res.users || {})
      }
    }));
    if (pointsDelta !== 0) {
      addUserPoints({
        points: pointsDelta,
        photoPoints: pointsDelta
      }, undefined)(dispatch, getState, extra);
    }
    toast({
      message: pointsDelta > 0
        ? t('Your story was saved and you earned {{points}} photo points!', { points: pointsDelta })
        : t('Your story was saved successfully!'),
      style: StyleState.Success
    }, 5000)(dispatch, getState);
  } catch (err) {
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('story', 'unhandledError', 'update');
  }
};

export const deleteStory = () => async (dispatch: Dispatch, getState: () => State, _extra: ExtraArguments) => {
  const { stories, storyToDelete } = getState();
  dispatch(patch({
    stories: up.reject((s) => s.id === storyToDelete.id)
  }));
  const api = selectors.userApi(getState());
  try {
    dispatch(patch({ modal: null }));
    const { competitionUserScores } = await api.deleteStory(storyToDelete.id);
    trackEvent('story', 'delete');
    dispatch(patch({ competitionUserScores }));
  } catch (err) {
    dispatch(patch({ stories }));
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('story', 'unhandledError', 'delete');
  }
};

export const initializeStoryPage = (term?: string) => async (dispatch: Dispatch, getState: () => State, { history, i18n }: ExtraArguments) => {
  const { storyID } = getStoryParams(history.location, i18n);
  const state = getState();
  if (state.stories.some((s) => s.id === storyID)) return;

  dispatch(patch({ spinner: true }));
  const api = selectors.userApi(state);
  try {
    const { token } = state;
    const res = await api.getCompetitionStories(state.competition.id, 0, 0, 0, storyID, 1, term, token);
    dispatch(patch({
      ...res,
      storyLikes: mergeStoryReactionMaps(state.storyLikes, res.storyLikes),
      storyFlags: mergeStoryReactionMaps(state.storyFlags, res.storyFlags),
      storyComments: { ...(state.storyComments || {}), ...(res.storyComments || {}) },
      storyCommentsLikes: mergeStoryReactionMaps(state.storyCommentsLikes, res.storyCommentsLikes),
      storyCommentsFlags: mergeStoryReactionMaps(state.storyCommentsFlags, res.storyCommentsFlags),
      spinner: false
    }));
  } catch (err) {
    handleUnexpectedError(err)(dispatch, getState);
  }
};
